import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Check } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';

export type Step =
  | 'url-input'
  | 'loading'
  | 'review'
  | 'products'
  | 'matching'
  | 'results'
  | 'email';

export type LoadingStepStatus = 'pending' | 'loading' | 'done';

export type LoadingStepDef = {
  title: string;
  loadingSubtitle: string;
};

const BASE_STEPPER_STEPS = [
  { key: 'url-input', label: 'Enter\ncompany website' },
  { key: 'review', label: 'Review company\nprofile' },
  { key: 'products', label: 'Review\nproducts' },
  { key: 'results', label: 'Review applicable\nsources' },
] as const;

const EMAIL_STEP = { key: 'email', label: 'Create your\naccount' } as const;

export type StepperVariant = 'dark' | 'light';

export const OnboardingStepper: FC<{
  currentStep: Step;
  variant?: StepperVariant;
  /** Show the 4th "Create your account" step (default: true). Set to false for authenticated flows. */
  showEmailStep?: boolean;
}> = ({ currentStep, variant = 'dark', showEmailStep = true }) => {
  const steps = showEmailStep
    ? [...BASE_STEPPER_STEPS, EMAIL_STEP]
    : BASE_STEPPER_STEPS;

  const getStepperIndex = (step: Step): number => {
    if (step === 'url-input' || step === 'loading') return 0;
    if (step === 'review') return 1;
    if (step === 'products' || step === 'matching') return 2;
    if (step === 'results') return 3;
    if (step === 'email') return 4;
    return 0;
  };

  const currentIndex = getStepperIndex(currentStep);
  const isLight = variant === 'light';
  const width = showEmailStep
    ? 'w-[920px] max-w-[calc(100vw-2rem)]'
    : 'w-[780px] max-w-[calc(100vw-2rem)]';

  return (
    <div className={`flex items-start ${width}`}>
      {steps.map((stepDef, i) => {
        const isActive = i === currentIndex;
        const isCompleted = i < currentIndex;

        return (
          <div
            key={stepDef.key}
            className="flex-1 flex flex-col gap-1 items-center"
          >
            <div className="flex items-center w-full h-4">
              <div className="flex-1 h-4 flex items-center">
                {i > 0 && <div className="w-full h-px bg-neutral-300" />}
              </div>

              <div
                className={`size-4 rounded-full border-2 flex items-center justify-center ${
                  isCompleted
                    ? isLight
                      ? 'border-[#130e30] bg-[#130e30]'
                      : 'border-white bg-white'
                    : isActive
                      ? isLight
                        ? 'border-[#130e30] bg-transparent'
                        : 'border-white bg-transparent'
                      : 'border-neutral-400 bg-transparent'
                }`}
              >
                {isCompleted && (
                  <Check
                    className={`size-2.5 ${isLight ? 'text-white' : 'text-[#130e30]'}`}
                    strokeWidth={3}
                  />
                )}
              </div>

              <div className="flex-1 h-4 flex items-center">
                {i < steps.length - 1 && (
                  <div className="w-full h-px bg-neutral-300" />
                )}
              </div>
            </div>

            <span
              className={`text-sm text-center leading-[1.6] px-1 sm:px-2 whitespace-pre-line ${
                isActive
                  ? isLight
                    ? 'text-[#130e30] font-medium'
                    : 'text-white font-medium'
                  : 'text-neutral-400'
              }`}
            >
              {stepDef.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const PROFILE_LOADING_STEPS: LoadingStepDef[] = [
  {
    title: 'Website is valid and reachable',
    loadingSubtitle: 'Checking...',
  },
  {
    title: 'Analyzing your website',
    loadingSubtitle: 'Processing...',
  },
  {
    title: 'Discovering device characteristics',
    loadingSubtitle: 'Extracting information...',
  },
  {
    title: 'Generating regulatory profile',
    loadingSubtitle: 'Processing...',
  },
];

export const MATCHING_LOADING_STEPS: LoadingStepDef[] = [
  {
    title: 'Fetching more than 200 regulations',
    loadingSubtitle: 'Searching database...',
  },
  {
    title: 'Analyzing more than 1,000 ISO norms',
    loadingSubtitle: 'Checking applicability...',
  },
  {
    title: 'Comparing regulations to your company profile',
    loadingSubtitle: 'Analyzing relevance...',
  },
  {
    title: 'Selecting relevant regulations',
    loadingSubtitle: 'Finalizing recommendations...',
  },
];

export const StepIcon: FC<{ status: LoadingStepStatus }> = ({ status }) => {
  if (status === 'done') {
    return (
      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-300">
        <Check className="size-3.5 text-white" strokeWidth={3} />
      </div>
    );
  }
  if (status === 'loading') {
    return (
      <div className="size-6 shrink-0 rounded-full border-2 border-[#ffe228] border-t-transparent animate-spin" />
    );
  }
  return (
    <div className="size-6 shrink-0 rounded-full border-2 border-[#130e30]/20" />
  );
};

export const LoadingStepRow: FC<{
  def: LoadingStepDef;
  status: LoadingStepStatus;
}> = ({ def, status }) => {
  return (
    <FlexRow gap="4" alignItems="start">
      <div className="mt-0.5 shrink-0">
        <StepIcon status={status} />
      </div>
      <FlexCol gap="0">
        <span className="text-lg font-medium tracking-[-0.44px] text-[#130e30]">
          {def.title}
        </span>
        {status === 'loading' && (
          <span className="text-sm text-[#130e30]/60">
            {def.loadingSubtitle}
          </span>
        )}
      </FlexCol>
    </FlexRow>
  );
};

export function useLoadingSteps(
  isActive: boolean,
  apiDone: boolean,
  steps: LoadingStepDef[],
  /** Average delay per step in ms (default 4500, i.e. ~2-7s per step) */
  stepDelayMs?: { min: number; max: number }
) {
  const [completedCount, setCompletedCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepsLength = steps.length;
  const delayMin = stepDelayMs?.min ?? 2000;
  const delayMax = stepDelayMs?.max ?? 5000;

  const scheduleNext = useCallback(() => {
    const delay = delayMin + Math.random() * (delayMax - delayMin);
    timerRef.current = setTimeout(() => {
      setCompletedCount(prev => prev + 1);
    }, delay);
  }, [delayMin, delayMax]);

  useEffect(() => {
    if (!isActive) {
      setCompletedCount(0);
      return;
    }
    scheduleNext();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isActive, scheduleNext]);

  useEffect(() => {
    if (!isActive) return;

    if (apiDone) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setCompletedCount(stepsLength);
      return;
    }

    if (completedCount > 0 && completedCount < stepsLength - 1) {
      scheduleNext();
    }
  }, [completedCount, isActive, apiDone, scheduleNext, stepsLength]);

  const statuses: LoadingStepStatus[] = steps.map((_, i) => {
    if (i < completedCount) return 'done';
    if (i === completedCount) return 'loading';
    return 'pending';
  });

  return statuses;
}
